import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Collapse,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertLinkOutlinedIcon from "@mui/icons-material/InsertLinkOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import copy from "copy-to-clipboard";

import BrandedExportFlyer from "./BrandedExportFlyer";
import { decodeAllImages, rasterizeNode } from "../../utils/captureNode";
import CustomSwitch from "../GlobalStyles/CustomSwitch";
import {
  getMFPrimaryButtonSx,
  getMFSecondaryButtonSx,
} from "../../utils/dialogStyles";
import {
  standardDialogBackdropSx,
  getStandardDialogPaperSx,
} from "../StandardDialog";
import { shapeShell } from "../../theme/shape";
import {
  TEMPLATE,
  EXPORT_SCALE,
  MAX_EXPORT_DIMENSION,
  EXPORT_FORMATS,
  DEFAULT_EXPORT_FORMAT,
  ALL_TEMPLATE_SVGS,
  selectTemplateSvg,
} from "../../constants/brandedExport";
import { buildGalleryShareUrl } from "../../utils/brandedExportUrl";
import {
  generateQrDataUrl,
  fetchImageAsDataUrl,
  blobToDataUrl,
} from "../../utils/brandedExportAssets";
import { getTenantConfig } from "../../utils/tenantConfig";

const PREVIEW_WIDTH = 280;

/**
 * A single label + iOS-style switch row, matching the editor's furniture
 * toggles (CustomSwitch). Dims (non-interactive) until the preview is ready.
 */
function ToggleRow({ label, checked, onToggle, disabled }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.25,
        opacity: disabled ? 0.55 : 1,
        transition: "opacity 160ms ease",
      }}
    >
      <Typography sx={{ fontSize: "0.95rem", color: "rgba(0,0,0,0.82)" }}>
        {label}
      </Typography>
      <CustomSwitch checked={checked} onClick={onToggle} disabled={disabled} />
    </Box>
  );
}

/** Resolve whatever getSnapshot returned (Blob | data URL | http URL) to a data URL. */
async function normalizeSnapshot(result) {
  if (!result) return null;
  if (typeof result === "string") {
    if (result.startsWith("data:")) return result;
    return fetchImageAsDataUrl(result);
  }
  if (typeof Blob !== "undefined" && result instanceof Blob) {
    return blobToDataUrl(result);
  }
  return null;
}

/** Confirm a data URL decodes to a real, non-empty raster before we frame it. */
function decodeCheck(dataUrl) {
  return new Promise((resolve) => {
    if (!dataUrl) return resolve(false);
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth > 0 && img.naturalHeight > 0);
    img.onerror = () => resolve(false);
    img.src = dataUrl;
  });
}

/**
 * BrandedExportModal — additive branded-download surface, openable from the
 * editor and the gallery. Captures the plan raster ONCE on open, then lets the
 * user toggle logo/disclaimer/format with an instant preview. The branding is
 * baked into the fixed template artboards; the modal only composites the plan
 * and QR onto the chosen artboard.
 *
 * The partner-logo toggle only appears for tenants whose templates carry a
 * partner logo (currently Serhant); everyone else gets the plain artboards,
 * which still carry the MagicFurnish "powered by" branding.
 *
 * Every risky step degrades: capture failure → onLegacyFallback; QR failure →
 * fall back to the no-qr artboard; never frames a broken raster.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => Promise<Blob|string|null>} props.getSnapshot - entry-point capture.
 * @param {string|number|null} props.layoutId - persisted id for the share URL.
 * @param {string|number|null} [props.version] - current version for the share URL.
 * @param {boolean} props.isSaved - when false, the QR is hidden (no dead links).
 * @param {string} [props.fileName] - download base name.
 * @param {() => void|Promise<void>} [props.onLegacyFallback] - legacy download.
 * @param {() => void} [props.onDownloaded] - download engagement callback.
 * @param {() => void} [props.onShared] - copy-link engagement callback (share).
 */
export default function BrandedExportModal({
  open,
  onClose,
  getSnapshot,
  layoutId,
  version,
  isSaved,
  fileName = "floor-plan",
  onLegacyFallback,
  onDownloaded,
  onShared,
  prepare,
}) {
  // Partner-branded templates are tenant-specific. Only Serhant currently ships
  // a logo variant, so the logo toggle is shown for Serhant only. Everyone else
  // gets the plain (MagicFurnish-branded) artboards.
  const tenantConfig = getTenantConfig();
  const isPartnerBranded = tenantConfig.tenantId === "serhant";
  // Wordmark for the logo toggle label (e.g. "SERHANT."), falling back to the
  // plain tenant name for any partner without a canonical display form.
  const partnerName = tenantConfig.brandDisplayName || tenantConfig.name;

  const [planImage, setPlanImage] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [templateMap, setTemplateMap] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | capturing | ready | error
  const [downloading, setDownloading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [showLogo, setShowLogo] = useState(isPartnerBranded);
  const [format, setFormat] = useState(DEFAULT_EXPORT_FORMAT);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Disclaimer is always on for now: the toggle (and the ability to export a
  // no-disclaimer template) is intentionally hidden so every flyer carries the
  // standard disclaimer. To restore the choice, make this `useState(false)`
  // and re-add the disclaimer ToggleRow in the controls section below.
  const showDisclaimer = true;

  const captureRef = useRef(null);
  const runIdRef = useRef(0);
  const linkCopiedTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (linkCopiedTimeoutRef.current) clearTimeout(linkCopiedTimeoutRef.current);
    },
    []
  );

  // Read getSnapshot from a ref so a caller passing an inline function (whose
  // identity changes every render) does not retrigger the one-time capture.
  const getSnapshotRef = useRef(getSnapshot);
  getSnapshotRef.current = getSnapshot;

  // Optional async hook the caller runs before capture (e.g. the editor
  // persists pending edits so the QR's gallery target matches the flyer). Read
  // from a ref so an inline caller function does not retrigger the open effect.
  const prepareRef = useRef(prepare);
  prepareRef.current = prepare;

  const shareUrl = useMemo(
    () => (isSaved ? buildGalleryShareUrl({ id: layoutId, version }) : null),
    [isSaved, layoutId, version]
  );

  // Copy-link target: the version-less canonical gallery URL, matching the
  // historical "Copy link" action exactly (the gallery ignores unknown params).
  const copyUrl = useMemo(
    () => (isSaved ? buildGalleryShareUrl({ id: layoutId }) : null),
    [isSaved, layoutId]
  );

  const handleCopyLink = useCallback(() => {
    if (!copyUrl) return;
    let ok = false;
    try {
      ok = copy(copyUrl);
    } catch (error) {
      ok = false;
    }
    if (!ok) return;
    setLinkCopied(true);
    // Analytics owned by the caller so the share count matches the legacy
    // "Copy link" path exactly (trackEngagement({ share: true }) + eventLog).
    onShared?.();
    if (linkCopiedTimeoutRef.current) clearTimeout(linkCopiedTimeoutRef.current);
    linkCopiedTimeoutRef.current = setTimeout(() => setLinkCopied(false), 2500);
  }, [copyUrl, onShared]);

  // One-time capture + asset resolution whenever the modal opens.
  useEffect(() => {
    if (!open) return;

    const runId = ++runIdRef.current;
    let cancelled = false;

    setStatus("capturing");
    setPlanImage(null);
    setQrDataUrl(null);

    (async () => {
      // Persist pending edits first (editor only) so the QR's gallery target
      // reflects the same layout as the freshly captured flyer. Bounded and
      // non-throwing by contract; a stalled save can never block the modal.
      if (prepareRef.current) {
        try {
          await prepareRef.current();
        } catch (error) {
          console.warn("[brandedExport] prepare failed:", error);
        }
        if (cancelled || runId !== runIdRef.current) return;
      }

      // Pre-resolve every template artboard to a data URL so toggling variants
      // is instant and the capture never has to fetch during export.
      const entries = await Promise.all(
        ALL_TEMPLATE_SVGS.map(async (url) => [url, await fetchImageAsDataUrl(url)])
      );
      if (cancelled || runId !== runIdRef.current) return;
      setTemplateMap(Object.fromEntries(entries));

      // QR (only when saved + a share URL exists).
      if (shareUrl) {
        const qr = await generateQrDataUrl(shareUrl);
        if (!cancelled && runId === runIdRef.current) setQrDataUrl(qr);
      }

      // Plan raster — the one capture. Validate before framing.
      try {
        const raw = await getSnapshotRef.current?.();
        const dataUrl = await normalizeSnapshot(raw);
        const ok = await decodeCheck(dataUrl);
        if (cancelled || runId !== runIdRef.current) return;
        if (ok) {
          setPlanImage(dataUrl);
          setStatus("ready");
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.warn("[brandedExport] snapshot capture failed:", error);
        if (!cancelled && runId === runIdRef.current) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
    // getSnapshot is intentionally read from a ref (see getSnapshotRef) so an
    // inline caller function does not retrigger the one-time capture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shareUrl]);

  const hasQr = !!qrDataUrl;
  const templateSrc = useMemo(() => {
    if (!templateMap) return null;
    const url = selectTemplateSvg({ hasQr, showLogo, showDisclaimer });
    return templateMap[url] || null;
  }, [templateMap, hasQr, showLogo, showDisclaimer]);

  const templateProps = { templateSrc, planImage, qrDataUrl };

  const handleDownload = useCallback(async () => {
    const node = captureRef.current;
    if (!node || status !== "ready") return;

    setDownloading(true);
    try {
      const maxDesign = Math.max(TEMPLATE.WIDTH, TEMPLATE.HEIGHT);
      const scale = Math.min(EXPORT_SCALE, MAX_EXPORT_DIMENSION / maxDesign);
      const width = Math.round(TEMPLATE.WIDTH * scale);
      const height = Math.round(TEMPLATE.HEIGHT * scale);

      const options = {
        width,
        height,
        quality: 1,
        skipFonts: true,
        cacheBust: false,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          margin: 0,
        },
      };

      // Deterministically decode every raster in the flyer, then raster
      // through the shared engine (one path for every download surface).
      await decodeAllImages(node);

      const dataUrl = await rasterizeNode(node, {
        ...options,
        as: format === EXPORT_FORMATS.PNG ? "png" : "jpeg",
      });

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${fileName || "floor-plan"}.${
        format === EXPORT_FORMATS.PNG ? "png" : "jpeg"
      }`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      onDownloaded?.();
    } catch (error) {
      console.error("[brandedExport] export failed:", error);
      // Last-resort fallback: let the caller run the legacy download.
      if (onLegacyFallback) {
        try {
          await onLegacyFallback();
          onClose?.();
        } catch (fallbackError) {
          console.error("[brandedExport] legacy fallback failed:", fallbackError);
        }
      }
    } finally {
      setDownloading(false);
    }
  }, [status, format, fileName, onDownloaded, onLegacyFallback, onClose]);

  const previewScale = PREVIEW_WIDTH / TEMPLATE.WIDTH;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        // Bottom-sheet on phones, centered card on tablet/desktop. Pure
        // flexbox alignment — no backdrop-filter, so there's no added
        // per-frame paint cost over the default centered dialog.
        "& .MuiDialog-container": {
          alignItems: { xs: "flex-end", sm: "center" },
        },
      }}
      // Standard MagicFurnish modal backdrop: transparent (no dark dim) — the
      // card is lifted off the page by shadow instead, matching every other
      // dialog (see StandardDialog).
      BackdropProps={{ sx: standardDialogBackdropSx }}
      PaperProps={{
        sx: (theme) => ({
          width: { xs: "100%", sm: 400 },
          maxWidth: { xs: "100%", sm: 400 },
          m: { xs: 0, sm: 3 },
          borderRadius: {
            xs: `${shapeShell} ${shapeShell} 0 0`,
            sm: shapeShell,
          },
          // Never taller than the viewport: cap height and let the body scroll
          // so content can never clip or overlap on short / landscape screens.
          maxHeight: { xs: "92dvh", sm: "calc(100dvh - 48px)" },
          overflowX: "hidden",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          bgcolor: "#ffffff",
          // Shared soft, layered modal shadow (replaces the dark scrim).
          boxShadow: getStandardDialogPaperSx(theme).boxShadow,
        }),
      }}
    >
      {/* Header — transparent so it blends into the preview surface */}
      <Box sx={{ position: "relative", px: 2, pt: { xs: 1.5, sm: 1.75 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1.0625rem",
              color: "rgba(0,0,0,0.85)",
            }}
          >
            Preview
          </Typography>
          <IconButton
            aria-label="Close"
            size="small"
            onClick={onClose}
            sx={{ color: "rgba(0,0,0,0.55)", mr: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Preview — floats on the surface, no chrome behind it */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
          pt: 2,
          pb: 1.5,
          minHeight: 200,
        }}
      >
        {status === "ready" ? (
          <Box
            sx={{
              width: PREVIEW_WIDTH,
              height: TEMPLATE.HEIGHT * previewScale,
              overflow: "hidden",
              borderRadius: "14px",
              boxShadow:
                "0 1px 2px rgba(0,0,0,0.06), 0 12px 32px rgba(0,0,0,0.14)",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                transform: `scale(${previewScale})`,
                transformOrigin: "top left",
              }}
            >
              <BrandedExportFlyer {...templateProps} />
            </Box>
          </Box>
        ) : status === "error" ? (
          <Stack alignItems="center" spacing={1.5} sx={{ px: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              We couldn't build the branded preview.
            </Typography>
            {onLegacyFallback && (
              <Button
                variant="outlined"
                size="small"
                onClick={async () => {
                  await onLegacyFallback();
                  onClose?.();
                }}
              >
                Download original
              </Button>
            )}
          </Stack>
        ) : (
          <CircularProgress size={28} />
        )}
      </Box>

      {/* Controls */}
      <Box
        sx={{
          px: { xs: 2.25, sm: 2.5 },
          pt: 1.25,
          pb: { xs: "max(18px, env(safe-area-inset-bottom, 18px))", sm: 2.5 },
        }}
      >
        {isPartnerBranded && (
          <ToggleRow
            label={`Add ${partnerName} logo`}
            checked={showLogo}
            onToggle={() => setShowLogo((v) => !v)}
            disabled={status !== "ready"}
          />
        )}

        {/* Advanced — file format tucked away to keep the surface calm. */}
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setShowAdvanced((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setShowAdvanced((v) => !v);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.25,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <Typography sx={{ fontSize: "0.9rem", color: "rgba(0,0,0,0.6)" }}>
            Advanced
          </Typography>
          <ExpandMoreRoundedIcon
            sx={{
              fontSize: 20,
              color: "rgba(0,0,0,0.5)",
              transform: showAdvanced ? "rotate(180deg)" : "none",
              transition: "transform 160ms ease",
            }}
          />
        </Box>
        <Collapse in={showAdvanced} unmountOnExit>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 1,
            }}
          >
            <Typography sx={{ fontSize: "0.9rem", color: "rgba(0,0,0,0.6)" }}>
              File format
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={format}
              onChange={(e, next) => next && setFormat(next)}
            >
              <ToggleButton
                value={EXPORT_FORMATS.JPEG}
                sx={{ px: 1.5, py: 0.25, textTransform: "none" }}
              >
                JPG
              </ToggleButton>
              <ToggleButton
                value={EXPORT_FORMATS.PNG}
                sx={{ px: 1.5, py: 0.25, textTransform: "none" }}
              >
                PNG
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Collapse>

        {!isSaved && (
          <Typography
            sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5, mb: 0.5 }}
          >
            Save your layout to include a QR code.
          </Typography>
        )}

        {/* Action row: Copy link (left) beside Download (right) to save height. */}
        <Stack direction="row" spacing={1.25} sx={{ mt: 2.25 }}>
          {copyUrl && (
            <Button
              variant="outlined"
              onClick={handleCopyLink}
              disabled={status !== "ready"}
              startIcon={
                <InsertLinkOutlinedIcon
                  fontSize="small"
                  sx={{ transform: "rotate(90deg)" }}
                />
              }
              sx={(theme) => ({
                ...getMFSecondaryButtonSx(theme),
                flex: "0 0 auto",
                px: 2,
                whiteSpace: "nowrap",
              })}
            >
              {linkCopied ? "Copied!" : "Copy link"}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={status !== "ready" || downloading}
            sx={(theme) => ({
              ...getMFPrimaryButtonSx(theme),
              flex: 1,
            })}
          >
            {downloading ? "Preparing…" : "Download flyer"}
          </Button>
        </Stack>
      </Box>

      {/* Off-screen full-size capture target (never transformed). */}
      {status === "ready" && (
        <Box
          aria-hidden
          sx={{
            position: "fixed",
            top: -100000,
            left: -100000,
            pointerEvents: "none",
          }}
        >
          <BrandedExportFlyer ref={captureRef} {...templateProps} />
        </Box>
      )}
    </Dialog>
  );
}
